import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronUp, ChevronDown } from 'lucide-react';

interface InPageSearchProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function InPageSearch({ containerRef }: InPageSearchProps) {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Range[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightClass = 'in-page-search-highlight';
  const activeClass = 'in-page-search-active';

  const clearHighlights = useCallback(() => {
    if (!containerRef.current) return;
    const marks = containerRef.current.querySelectorAll(`.${highlightClass}`);
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
        parent.normalize();
      }
    });
    setMatches([]);
    setCurrentIndex(0);
  }, [containerRef]);

  const highlightMatches = useCallback((searchText: string) => {
    clearHighlights();
    if (!containerRef.current || !searchText.trim()) return;

    const walker = document.createTreeWalker(
      containerRef.current,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const textNodes: Text[] = [];
    let node: Text | null;
    while ((node = walker.nextNode() as Text)) {
      if (node.nodeValue && node.nodeValue.toLowerCase().includes(searchText.toLowerCase())) {
        textNodes.push(node);
      }
    }

    const allMarks: HTMLElement[] = [];
    for (const textNode of textNodes) {
      const text = textNode.nodeValue || '';
      const lower = text.toLowerCase();
      const searchLower = searchText.toLowerCase();
      const parts: (string | { match: string })[] = [];
      let lastIndex = 0;
      let idx = lower.indexOf(searchLower);

      while (idx !== -1) {
        if (idx > lastIndex) parts.push(text.slice(lastIndex, idx));
        parts.push({ match: text.slice(idx, idx + searchText.length) });
        lastIndex = idx + searchText.length;
        idx = lower.indexOf(searchLower, lastIndex);
      }
      if (lastIndex < text.length) parts.push(text.slice(lastIndex));

      if (parts.length <= 1) continue;

      const fragment = document.createDocumentFragment();
      for (const part of parts) {
        if (typeof part === 'string') {
          fragment.appendChild(document.createTextNode(part));
        } else {
          const mark = document.createElement('mark');
          mark.className = `${highlightClass} search-highlight`;
          mark.textContent = part.match;
          allMarks.push(mark);
          fragment.appendChild(mark);
        }
      }
      textNode.parentNode?.replaceChild(fragment, textNode);
    }

    setMatches(allMarks as unknown as Range[]);
    if (allMarks.length > 0) {
      setCurrentIndex(0);
      allMarks[0].classList.add(activeClass, 'current');
      allMarks[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [containerRef, clearHighlights]);

  const navigateMatch = useCallback((direction: 'prev' | 'next') => {
    if (!containerRef.current) return;
    const marks = containerRef.current.querySelectorAll(`.${highlightClass}`);
    if (marks.length === 0) return;

    // Remove active from current
    marks[currentIndex]?.classList.remove(activeClass, 'current');

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % marks.length;
    } else {
      newIndex = (currentIndex - 1 + marks.length) % marks.length;
    }

    marks[newIndex]?.classList.add(activeClass, 'current');
    marks[newIndex]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setCurrentIndex(newIndex);
  }, [containerRef, currentIndex]);

  const close = useCallback(() => {
    setVisible(false);
    setQuery('');
    clearHighlights();
  }, [clearHighlights]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setVisible(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && visible) {
        close();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, close]);

  useEffect(() => {
    if (query.length >= 2) {
      highlightMatches(query);
    } else {
      clearHighlights();
    }
  }, [query, highlightMatches, clearHighlights]);

  if (!visible) return null;

  const markCount = containerRef.current?.querySelectorAll(`.${highlightClass}`).length || 0;

  return (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-2">
      <input
        ref={inputRef}
        type="text"
        placeholder="Find in page..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            navigateMatch(e.shiftKey ? 'prev' : 'next');
          }
        }}
        className="w-48 px-2 py-1 text-sm bg-transparent border-none outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
      />
      {query && (
        <span className="text-xs text-slate-500 shrink-0">
          {markCount > 0 ? `${currentIndex + 1}/${markCount}` : '0/0'}
        </span>
      )}
      <button onClick={() => navigateMatch('prev')} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Previous">
        <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
      </button>
      <button onClick={() => navigateMatch('next')} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Next">
        <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
      </button>
      <button onClick={close} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Close">
        <X className="w-3.5 h-3.5 text-slate-500" />
      </button>
    </div>
  );
}
